import * as React from "react";
import { AlertTriangle, Info, RotateCcw } from "lucide-react";
import { Button, Eyebrow, Panel, Section, cn } from "@ozpanel/ui";
import { FieldInfo } from "./FieldInfo";

// DataForm — D7's schema-driven half, finally built. Renders a module's OWN declared
// SettingsSpec (internal/module/settings.go), so all 19 modules that implement Settings get a
// real, validated settings screen from ONE component rather than 19 hand-built ones. This is
// the difference between "the UI has some screens" and "the UI manages the platform".
//
// It renders what the SERVER declares and nothing more: field types, help text, placeholders,
// and — importantly — server-RESOLVED choices (OptionsFrom), which exist precisely because
// "which address should this bind to" is a question only the running node can answer. A bare
// text box for that is how a service ends up refusing to start.

export type FieldType = "string" | "int" | "bool" | "enum" | "duration" | "size";

export interface Choice {
  value: string;
  label: string;
  detail?: string;
  risk?: string;
}

export interface SettingsField {
  key: string;
  label: string;
  type: FieldType | string;
  help?: string;
  options?: string[];
  placeholder?: string;
  options_from?: string;
  choices?: Choice[];
  /** "cluster": the value is the cluster's — saved once, applied on every node (modsettings). */
  scope?: string;
  /** The platform's value for a cluster field nobody has chosen. */
  cluster_default?: string;
}

export interface SettingsGroup {
  title: string;
  fields: SettingsField[];
}

export interface SettingsSpec {
  title: string;
  note?: string;
  raw_path?: string;
  info_path?: string;
  param_path?: string;
  maps_path?: string;
  groups: SettingsGroup[];
}

export type SettingsValues = Record<string, unknown>;

/** Only the CHANGED keys are submitted — WriteSettings takes a change set, and sending the
 *  whole form back would re-write directives the operator never touched. */
function changedOnly(values: SettingsValues, original: SettingsValues): SettingsValues {
  const out: SettingsValues = {};
  for (const [k, v] of Object.entries(values)) {
    if (JSON.stringify(v) !== JSON.stringify(original[k])) out[k] = v;
  }
  return out;
}

export function DataForm({
  spec,
  values: initial,
  onSave,
  saving,
  disabled,
  module,
  configFile,
  overrides,
  defaults,
}: {
  spec: SettingsSpec;
  values: SettingsValues;
  onSave(changes: SettingsValues): void | Promise<void>;
  saving?: boolean;
  disabled?: boolean;
  /** Names the directive vocabulary in the per-field reference, e.g. "postfix". */
  module?: string;
  /** Where values are written, shown in the per-field reference. */
  configFile?: string;
  /** Per-service overrides keyed by field key, e.g. postfix master.cf `-o` lines. A field
   *  that has these is NOT described by its global value alone. */
  overrides?: Record<string, Record<string, string>>;
  /** Fields currently at the service's DEFAULT (not explicitly set), keyed to the effective
   *  default value. Present-but-empty means at a default with no concrete value. The form tags
   *  these so an operator can tell a value they set from one that is merely the daemon's default. */
  defaults?: Record<string, string>;
}) {
  const [values, setValues] = React.useState<SettingsValues>(initial);

  // A refetch after saving must reset the baseline, or the form stays permanently "dirty".
  React.useEffect(() => setValues(initial), [initial]);

  const changes = changedOnly(values, initial);
  const dirtyKeys = Object.keys(changes);
  const set = (k: string, v: unknown) => setValues((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-4">
      {spec.note && (
        <p className="rounded-card border border-line bg-bg2 px-4 py-3 text-[13px] leading-relaxed text-muted">
          {spec.note}
        </p>
      )}

      {/* Collapsible, and only the FIRST group open by default. Postfix declares 65 fields
          across ten groups; rendering all of them expanded made the page unreadable and buried
          the save bar under several screens of scroll. Each header still says how many fields
          it holds and how many are unsaved, so a closed group never hides a pending change. */}
      {spec.groups.map((g, gi) => {
        const groupChanged = g.fields.filter((f) => f.key in changes).length;
        return (
        <Section
          key={g.title}
          title={g.title}
          collapsible
          id={`form.${spec.title}.${g.title}`}
          defaultOpen={gi === 0}
          bodyClassName="p-0"
          hint={`${g.fields.length} setting${g.fields.length === 1 ? "" : "s"}`}
          actions={
            groupChanged > 0 ? (
              <span className="rounded-full bg-accentsoft px-2 py-0.5 font-mono text-[10px] text-accent">
                {groupChanged} changed
              </span>
            ) : undefined
          }
        >
          <div className="divide-y divide-line">
            {g.fields.map((f) => (
              <Row
                key={f.key}
                field={f}
                value={values[f.key]}
                dirty={f.key in changes}
                onChange={(v) => set(f.key, v)}
                disabled={disabled}
                module={module}
                configFile={configFile}
                overrides={overrides?.[f.key]}
                atDefault={defaults && Object.prototype.hasOwnProperty.call(defaults, f.key)}
                defaultValue={defaults?.[f.key]}
              />
            ))}
          </div>
        </Section>
        );
      })}

      {/* A sticky action bar, because these forms are long and a save button at the bottom of
          twelve groups is a save button nobody finds. Only appears when there is something to
          save — and says exactly how many fields changed. */}
      {dirtyKeys.length > 0 && (
        <div className="sticky bottom-4 z-10 flex items-center justify-between gap-3 rounded-card border border-[var(--ozp-accent-line)] bg-surface px-4 py-3 shadow-[0_18px_50px_-12px_rgba(0,0,0,.5)]">
          <span className="text-[13px] text-muted">
            <span className="font-semibold text-fg">{dirtyKeys.length}</span> unsaved change
            {dirtyKeys.length === 1 ? "" : "s"}
            <span className="ml-2 font-mono text-[11.5px] text-faint">{dirtyKeys.join(", ")}</span>
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={saving} onClick={() => setValues(initial)}>
              <RotateCcw size={13} />
              Revert
            </Button>
            <Button size="sm" variant="accent" disabled={saving} onClick={() => void onSave(changes)}>
              {saving ? "Applying…" : "Apply changes"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** STRENGTH ordering for the few security parameters where "different" is not enough and the
 *  DIRECTION matters. An override that raises the bar is reassurance; one that lowers it is a
 *  finding. Anything not listed here is reported neutrally rather than guessed at. */
const STRENGTH: Record<string, string[]> = {
  // weakest → strongest
  smtpd_tls_security_level: ["none", "may", "encrypt"],
  smtp_tls_security_level: ["none", "may", "encrypt"],
  smtpd_tls_auth_only: ["no", "yes"],
  smtpd_sasl_auth_enable: ["no", "yes"],
};

function weakerOverride(field: SettingsField, value: unknown, overrides: Record<string, string>): boolean {
  const order = STRENGTH[field.key];
  if (!order) return false;
  const globalRank = order.indexOf(String(value ?? ""));
  if (globalRank < 0) return false;
  return Object.values(overrides).some((v) => {
    const r = order.indexOf(String(v));
    return r >= 0 && r < globalRank;
  });
}

function Row({
  field,
  value,
  dirty,
  onChange,
  disabled,
  module,
  configFile,
  overrides,
  atDefault,
  defaultValue,
}: {
  field: SettingsField;
  value: unknown;
  dirty: boolean;
  onChange(v: unknown): void;
  disabled?: boolean;
  module?: string;
  configFile?: string;
  overrides?: Record<string, string>;
  atDefault?: boolean;
  defaultValue?: string;
}) {
  const choice = field.choices?.find((c) => c.value === String(value ?? ""));
  return (
    <div className={cn("grid gap-4 px-4 py-3 sm:grid-cols-[minmax(220px,1fr)_minmax(200px,320px)]", dirty && "bg-accentsoft/40")}>
      <div className="min-w-0">
        <label htmlFor={field.key} className="block text-[13.5px] font-medium text-fg">
          {field.label}
          <FieldInfo field={field} value={value} module={module} file={configFile} overrides={overrides} />
          {field.scope === "cluster" && (
            <span
              className="ml-2 rounded bg-accentsoft px-1.5 py-0.5 font-mono text-[10px] text-accent"
              title={
                "Cluster-wide: saving applies it here first, then every node applies it on its next pass (including nodes added later). " +
                "The cross-node check above shows what each node actually runs." +
                (field.cluster_default ? ` Platform default: ${field.cluster_default}.` : "")
              }
            >
              cluster-wide
            </span>
          )}
          {dirty && <span className="ml-2 font-mono text-[10px] text-accent">changed</span>}
          {atDefault && !dirty && (
            <span
              className="ml-2 rounded bg-line/50 px-1.5 py-0.5 font-mono text-[10px] text-muted"
              title="At the service's default — this value is not explicitly set, so the daemon may change its own default in a future version."
            >
              default{defaultValue ? `: ${defaultValue}` : ""}
            </span>
          )}
        </label>
        {field.help && <p className="mt-1 text-[12px] leading-relaxed text-muted">{field.help}</p>}
        {/* The risk attached to the CURRENTLY SELECTED choice, surfaced inline — a resolved
            option's warning is useless if it is only visible while the dropdown is open. */}
        {choice?.risk && (
          <p className="mt-1.5 flex items-start gap-1.5 text-[12px] text-warn">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {choice.risk}
          </p>
        )}
        {choice?.detail && !choice.risk && (
          <p className="mt-1.5 font-mono text-[11.5px] text-faint">{choice.detail}</p>
        )}
        {/* Interlock: never show a value without what governs it. This global is overridden
            for some services, so on its own it describes none of them fully.
            Tone matters: on this platform the overrides are deliberate HARDENING (submission
            requires auth and TLS while port 25, the MX, must not). Painting that amber would
            report a correctly configured node as a problem — the same false-alarm mistake the
            confcheck requirement for submission/smtpd_sasl_auth_enable documents. So this is
            informational, and only turns amber when the override is genuinely WEAKER than the
            global it replaces. */}
        {overrides && Object.keys(overrides).length > 0 && (
          <p
            className={cn(
              "mt-2 flex items-start gap-1.5 rounded-md border px-2 py-1.5 text-[11.5px]",
              weakerOverride(field, value, overrides)
                ? "border-[color-mix(in_oklab,var(--ozp-warn)_35%,transparent)] bg-warnsoft text-warn"
                : "border-line bg-bg2 text-muted",
            )}
          >
            {weakerOverride(field, value, overrides) ? (
              <AlertTriangle size={11} className="mt-0.5 shrink-0" />
            ) : (
              <Info size={11} className="mt-0.5 shrink-0" />
            )}
            <span>
              Overridden for{" "}
              {Object.entries(overrides).map(([svc, v], i) => (
                <React.Fragment key={svc}>
                  {i > 0 && ", "}
                  <span className="font-mono">{svc.split("/")[0]}</span>
                  {" = "}
                  <span className="font-mono">{v}</span>
                </React.Fragment>
              ))}
              . This global value does not apply there.
            </span>
          </p>
        )}
      </div>
      <div className="min-w-0">
        <Control field={field} value={value} onChange={onChange} disabled={disabled} />
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-line2 bg-bg px-3 py-2 font-mono text-[13px] text-fg outline-none transition-colors focus:border-accent disabled:opacity-50";

function Control({
  field,
  value,
  onChange,
  disabled,
}: {
  field: SettingsField;
  value: unknown;
  onChange(v: unknown): void;
  disabled?: boolean;
}) {
  // Server-resolved choices win over a static option list: they are the ones that know what
  // this node can actually do.
  const choices: Choice[] | undefined =
    field.choices?.length ? field.choices : field.options?.map((o) => ({ value: o, label: o }));

  if (field.type === "bool") {
    const on = value === true || value === "yes" || value === "true";
    return (
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={field.label}
        disabled={disabled}
        onClick={() => onChange(!on)}
        className={cn(
          "relative h-6 w-[42px] rounded-full border transition-colors disabled:opacity-50",
          on ? "border-[var(--ozp-accent-line)] bg-accentsoft" : "border-line2 bg-surface3",
        )}
      >
        <span
          className={cn(
            "absolute top-[3px] size-4 rounded-full transition-transform",
            on ? "translate-x-[21px] bg-accent" : "translate-x-[3px] bg-muted",
          )}
        />
      </button>
    );
  }

  if (choices && choices.length > 0) {
    return (
      <select
        id={field.key}
        disabled={disabled}
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
      >
        {/* An unset value must be representable, or opening the form silently picks the first
            option and the next save writes a directive nobody chose. */}
        {/* An unset value must be representable, or opening the form silently picks the
            first option and the next save writes a directive nobody chose. Empty STRING counts
            as unset too — `??` alone misses it, which rendered a blank dropdown. */}
        {!choices.some((c) => c.value === String(value ?? "")) && (
          <option value={String(value ?? "")}>
            {value === null || value === undefined || value === "" ? "— not set —" : String(value)}
          </option>
        )}
        {choices.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      id={field.key}
      disabled={disabled}
      type={field.type === "int" ? "number" : "text"}
      value={value === null || value === undefined ? "" : String(value)}
      placeholder={field.placeholder}
      onChange={(e) => {
        const raw = e.target.value;
        onChange(field.type === "int" ? (raw === "" ? "" : Number(raw)) : raw);
      }}
      className={inputCls}
    />
  );
}
